import React from "react";
import { Link } from "react-router-dom";
import { Star, Quote, Instagram } from "lucide-react";

/* ============================================================
 * REAL REVIEWS from customers — replace with actual Google reviews.
 * To add a review, drop a new object into REVIEWS below. To add a
 * customer photo review, drop a URL into PHOTO_REVIEWS.
 * ============================================================ */
const REVIEWS = [
  {
    name: "Hilda Ramirez",
    date: "3 months ago",
    stars: 5,
    text: "I'm so grateful for Angy. The flower arrangements were stunning and exceeded my expectations. Every detail was thoughtfully put together, and you can see the passion behind her work. She helped make such a special moment even more meaningful. I truly appreciate her kindness and talent.",
    tone: "pink",
    tag: "flower arrangements",
  },
  {
    name: "Andy Tran",
    date: "3 weeks ago",
    stars: 5,
    text: "Angy did such a great job! Ordered the flowers with a budget in mind and told her what my gf loves flower-wise. Flowers were ready on pick date and time. Will be back for Valentine's Day!",
    tone: "mint",
    tag: "budget-friendly",
  },
  {
    name: "Juan Carlos Bautista Lagunas",
    date: "5 months ago",
    stars: 5,
    text: "I've been buying flowers from Angy for the past 2-3 years. She does a very wonderful job all the time. The flowers are always fresh and nice. My fiancée loves the flower arrangements/bouquets all the time. You should also visit her shop.",
    tone: "cream",
    tag: "fresh flowers",
  },
  {
    name: "A happy customer",
    date: "Verified Google review",
    stars: 5,
    text: "Very nice and beautiful place, very nice employees and prices are unbeatable.",
    tone: "lav",
    tag: "unbeatable prices",
  },
  {
    name: "A happy customer",
    date: "Verified Google review",
    stars: 5,
    text: "Great selection of flowers, and very friendly environment!",
    tone: "pink",
    tag: "friendly environment",
  },
  {
    name: "A happy customer",
    date: "Verified Google review",
    stars: 5,
    text: "So glad I found this place and can't wait to get all my flowers from here!",
    tone: "mint",
    tag: "loyal customer",
  },
];

/* Customer picture reviews — array of image URLs from your Google Business profile */
const PHOTO_REVIEWS = [];

const STAR = ({ filled }) => (
  <Star
    size={16}
    fill={filled ? "#e8b74a" : "none"}
    stroke={filled ? "#c9a24f" : "#d7ccc0"}
    strokeWidth={1.5}
  />
);

const TONE_BG = {
  pink: "var(--lf-pink-pale)",
  mint: "var(--lf-mint)",
  cream: "var(--lf-cream-2)",
  lav: "var(--lf-lavender)",
};

const Reviews = () => {
  return (
    <div data-testid="reviews-page" style={{ background: "var(--lf-cream)", minHeight: "100vh" }}>
      {/* Hero header */}
      <section
        className="relative overflow-hidden"
        style={{ background: "var(--lf-pink-pale)" }}
      >
        <div className="lf-container pt-20 md:pt-28 pb-16 md:pb-20 text-center relative z-10">
          <span className="lf-eyebrow" data-testid="reviews-eyebrow">Customer love</span>
          <h1
            className="font-cursive lf-gold-shimmer mt-4"
            style={{ fontSize: "clamp(3rem, 8vw, 6.5rem)" }}
            data-testid="reviews-title"
          >
            Reviews
          </h1>
          <p
            className="font-serif italic mt-4 max-w-2xl mx-auto leading-relaxed"
            style={{ fontSize: "clamp(1.05rem, 1.6vw, 1.25rem)", color: "var(--lf-ink)" }}
          >
            At <em style={{ color: "var(--lf-gold-deep)" }}>La Fleur</em>, every bouquet
            leaves our studio with a little piece of our heart in it. We treat every
            order &mdash; from a single stem to a 200-rose signature &mdash; like it&rsquo;s for our own
            family. Your kind words and photos are what make this small shop feel like
            the whole world, and we&rsquo;re endlessly grateful for the community that keeps
            walking through our door. &#128144;
          </p>

          {/* Rating summary — from your live Google Business Profile */}
          <div className="mt-8 inline-flex items-center gap-3 px-6 py-3" style={{ background: "white", borderRadius: 999, boxShadow: "0 6px 20px -8px rgba(58,46,42,0.15)" }}>
            <div className="flex items-center gap-0.5">
              {[1,2,3,4,5].map((n) => <STAR key={n} filled />)}
            </div>
            <span className="font-serif text-lg" style={{ color: "var(--lf-ink)" }}>
              <strong style={{ color: "var(--lf-gold-deep)" }}>4.8</strong> stars · <strong>58</strong> Google reviews
            </span>
          </div>

          <div className="mt-6" data-testid="reviews-cta-row">
            <a
              href="https://g.page/r/CagnLOLBjk08EAE/review"
              target="_blank"
              rel="noreferrer"
              className="lf-btn lf-btn-gold"
              data-testid="leave-review-btn"
            >
              Leave us a review on Google
            </a>
          </div>
        </div>
      </section>

      {/* Written reviews */}
      <section className="py-20 md:py-24">
        <div className="lf-container">
          <div className="text-center mb-14">
            <span className="lf-eyebrow" style={{ color: "var(--lf-pink-deep)" }}>Kind words</span>
            <h2 className="font-serif mt-3" style={{ fontSize: "clamp(1.75rem, 4vw, 2.75rem)", fontWeight: 300, color: "var(--lf-ink)" }}>
              From our <em style={{ color: "var(--lf-gold-deep)" }}>customers.</em>
            </h2>
          </div>

          {REVIEWS.length === 0 ? (
            <div className="text-center py-16" style={{ color: "var(--lf-ink-soft)" }}>
              Reviews coming soon.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {REVIEWS.map((r, i) => (
                <article
                  key={i}
                  data-testid={`review-card-${i}`}
                  className="p-7 relative"
                  style={{
                    background: TONE_BG[r.tone] || "white",
                    borderRadius: 24,
                    border: "1px solid rgba(0,0,0,0.04)",
                  }}
                >
                  <Quote
                    size={28}
                    style={{ color: "var(--lf-gold-deep)", opacity: 0.45, position: "absolute", top: 20, right: 22 }}
                  />
                  <div className="flex items-center gap-0.5 mb-3">
                    {[1,2,3,4,5].map((n) => <STAR key={n} filled={n <= r.stars} />)}
                  </div>
                  <p className="font-serif leading-relaxed text-base" style={{ color: "var(--lf-ink)" }}>
                    “{r.text}”
                  </p>
                  <div className="mt-5 pt-4" style={{ borderTop: "1px solid rgba(0,0,0,0.06)" }}>
                    <div className="font-serif" style={{ color: "var(--lf-ink)" }}>{r.name}</div>
                    {r.date && (
                      <div className="text-xs mt-0.5" style={{ color: "var(--lf-ink-soft)" }}>
                        {r.date}{r.source ? ` · ${r.source}` : " · Google review"}
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Picture reviews */}
      <section className="pb-20 md:pb-28" data-testid="picture-reviews-section">
        <div className="lf-container">
          <div className="text-center mb-12">
            <span className="lf-eyebrow" style={{ color: "var(--lf-pink-deep)" }}>Straight from you</span>
            <h2 className="font-serif mt-3" style={{ fontSize: "clamp(1.75rem, 4vw, 2.75rem)", fontWeight: 300, color: "var(--lf-ink)" }}>
              Customer <em style={{ color: "var(--lf-gold-deep)" }}>moments.</em>
            </h2>
            <p className="mt-3 text-sm max-w-lg mx-auto" style={{ color: "var(--lf-ink-soft)" }}>
              Real photos our customers posted on Google. Thank you for sharing your bouquets with the world. 🌸
            </p>
          </div>

          {PHOTO_REVIEWS.length === 0 ? (
            <div
              className="max-w-lg mx-auto text-center py-14 px-6"
              style={{ background: "white", borderRadius: 24, border: "1px dashed var(--lf-gold)" }}
            >
              <Instagram size={28} style={{ color: "var(--lf-gold-deep)", margin: "0 auto 12px" }} />
              <p className="font-serif italic" style={{ color: "var(--lf-ink)" }}>
                Customer picture reviews coming soon.
              </p>
              <p className="text-sm mt-2" style={{ color: "var(--lf-ink-soft)" }}>
                We&rsquo;re gathering the most beautiful photos our customers have posted on Google to share here.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
              {PHOTO_REVIEWS.map((url, i) => (
                <div
                  key={i}
                  data-testid={`picture-review-${i}`}
                  className="overflow-hidden"
                  style={{
                    aspectRatio: [3/4, 1, 4/5, 3/4][i % 4],
                    borderRadius: 18,
                    background: "var(--lf-pink-pale)",
                  }}
                >
                  <img src={url} alt={`Customer picture review ${i + 1}`} className="w-full h-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA footer */}
      <section className="pb-24" data-testid="reviews-footer-cta">
        <div className="lf-container">
          <div className="p-10 md:p-14 text-center" style={{ background: "var(--lf-pink-pale)", borderRadius: 28 }}>
            <p className="font-script text-3xl md:text-4xl" style={{ color: "var(--lf-pink-deep)" }}>
              thank you for choosing us
            </p>
            <p className="font-serif italic mt-3 max-w-lg mx-auto" style={{ color: "var(--lf-ink)" }}>
              Your trust means the world. Ready to bring a little bloom into someone&rsquo;s day?
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/shop" className="lf-btn lf-btn-gold">Shop bouquets</Link>
              <Link to="/services/flower-bar" className="lf-btn lf-btn-outline">Visit the Flower Bar</Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Reviews;
